import { useSession } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useContext } from 'react'
import { match, P } from 'ts-pattern'
import { AuthModalContext } from 'components/Auth/AuthModalContext'
import { useFinalPrice } from 'components/Hook/useFinalPrice'
import { Status } from 'components/common/Status/Status'
import { Sponsor } from 'domain/models/Sponsor/Sponsor'
import { isNonEmptyArray } from 'helpers/array'
import { LiveEventRace } from 'services/api/database/EventService'
import { FullRaceCardContent } from './FullRaceCardContent'
import { ResumeRaceCardContent } from './ResumeRaceCardContent'

interface RaceCardProps {
  race: LiveEventRace
  ctaLink: string
  raceStatus: Status
  sponsors?: Sponsor[]
  cardType: 'FULL' | 'RESUME'
  isFeatured: boolean | null
  showRaceStock: boolean
}

enum CalendarStatus {
  CONFIRMED = 'CONFIRMED',
  CANCELED = 'CANCELED',
  TO_BE_CONFIRMED = 'TO_BE_CONFIRMED'
}

export default function RaceCard({
  race,
  ctaLink,
  sponsors,
  raceStatus,
  cardType,
  isFeatured,
  showRaceStock
}: RaceCardProps) {
  const { t } = useTranslation(['common', 'event'])
  const { price, priceWithReduction } = race

  const session = useSession()

  const { setModalParams } = useContext(AuthModalContext)

  const isValidPrice =
    (!!price || price === 0) &&
    race.edition?.calendarStatus !== CalendarStatus.TO_BE_CONFIRMED

  const handleClick = () => {
    if (!ctaLink.startsWith('/')) {
      window.open(ctaLink, '_blank')
    } else if (sponsors && isNonEmptyArray(sponsors)) {
      setModalParams({
        state: 'SPONSOR',
        redirectUrl: ctaLink,
        sponsors
      })
    } else if (session.status === 'authenticated') {
      window.open(ctaLink, '_self')
    } else {
      setModalParams({
        state: 'SIGNIN',
        redirectUrl: ctaLink,
        sponsors
      })
    }
  }

  const finalPrice = useFinalPrice(price ?? 0)
  const finalReductionPrice = useFinalPrice(priceWithReduction ?? 0)

  const hasReduction =
    priceWithReduction !== null && priceWithReduction !== price
  const isWaitingList = raceStatus === Status.WAITING_LIST

  const RaceCardContent = match(cardType)
    .with('FULL', () => FullRaceCardContent)
    .with('RESUME', () => ResumeRaceCardContent)
    .exhaustive()

  const ctaLabel = match({
    raceStatus,
    isFeatured,
    registrationOpeningDate: race.registrationOpeningDate
  })
    .with(
      P.when(
        ({ registrationOpeningDate }) =>
          registrationOpeningDate &&
          new Date(registrationOpeningDate) > new Date()
      ),
      () => t('eventPage.raceCards.comingSoon')
    )
    .with({ raceStatus: Status.OUT_OF_STOCK }, () => t('OUT_OF_STOCK'))
    .with({ isFeatured: true, raceStatus: Status.ONLINE }, () =>
      t('eventPage.raceCards.signUp')
    )
    .with({ isFeatured: true }, () => t('event:closedSubscriptions'))
    .otherwise(() => t('eventPage.raceCards.moreInformation'))

  return (
    <>
      <RaceCardContent
        isValidPrice={isValidPrice}
        isWaitingList={isWaitingList}
        finalPrice={finalPrice}
        hasReduction={hasReduction}
        finalReductionPrice={finalReductionPrice}
        ctaLink={ctaLink}
        raceStatus={raceStatus}
        handleClick={handleClick}
        race={race}
        isFeatured={isFeatured}
        ctaLabel={ctaLabel}
        showRaceStock={showRaceStock}
      />
    </>
  )
}
